import { NewRegistrationFlowProps, RegistrationFlowProps } from '@/app/modules/RegistrationFlow';
import { getCountryFromIpAddress } from '@/app/services/ipLocationService';
import { appServerConfig } from '@/app/utils/appServerConfig';
import {
  getNewRegistrationFlowStatus,
  getNewRegistrationSpecialityScreenRemovedStatus,
  getNomanEasterEggFlag,
  getProfileRegistrationAbTestFlag,
  getProfileRegistrationFromJobEnquiryFlag,
  getRedirectRemainingQueryActionToProfileRegistration,
} from '@/app/utils/getServerFeatureFlag';
import { RedirectionPreAction } from '@/app/utils/urlHelper';
import { WhitepaperSlugs, whitepapers } from '@/modules/Whitepapers/whitepapers';
import { tryToNoticeError } from '@/utils/errorInfo';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import dynamic from 'next/dynamic';
import React from 'react';
import { enforceSafeRedirectUrl } from 'utils/enforceSafeRedirectUrl';

const RegistrationFlowNew = dynamic<NewRegistrationFlowProps>(
  () => import('@/modules/RegistrationFlow/registration-flow-new'),
);
const RegistrationFlowOld = dynamic<RegistrationFlowProps>(
  () => import('@/modules/RegistrationFlow/registration-flow-old'),
);

type RegisterPageProps = RegistrationFlowProps & {
  isNewRegistrationFlowEnabled: boolean;
  isNewRegistrationSpecialityScreenRemoved: boolean;
};

const Register = ({
  isNewRegistrationFlowEnabled,
  isNewRegistrationSpecialityScreenRemoved,
  isNomanEasterEggEnabled,
  ...registrationFlowProps
}: RegisterPageProps) => {
  return (
    <>
      {isNewRegistrationFlowEnabled ? (
        <RegistrationFlowNew
          specialityScreenRemoved={isNewRegistrationSpecialityScreenRemoved}
          {...registrationFlowProps}
        />
      ) : (
        <RegistrationFlowOld
          isNomanEasterEggEnabled={isNomanEasterEggEnabled}
          {...registrationFlowProps}
        />
      )}
    </>
  );
};

function generatePractitionerRegistrationUrl(
  action: string | undefined,
  jobNumber: string | undefined,
  jobUrl: string | undefined,
  previousRoute: string | null,
) {
  const redirectParams = new URLSearchParams();
  if (action) {
    redirectParams.append('action', action);
  }
  if (jobNumber) {
    redirectParams.append('job_number', jobNumber);
  }
  if (jobUrl) {
    redirectParams.append('job_url', jobUrl);
  }
  if (previousRoute) {
    redirectParams.append('back_to', 'medrecruit');
    redirectParams.append('redirect_to', previousRoute);
  }

  return redirectParams.size === 0
    ? `${appServerConfig.practitionerProfileUrl}/register`
    : `${appServerConfig.practitionerProfileUrl}/register?${redirectParams.toString()}`;
}

async function getRedirectIfRequired(
  context: GetServerSidePropsContext,
  previousRoute: string | null,
): Promise<GetServerSidePropsResult<RegisterPageProps> | undefined> {
  const { query } = context;
  //  A2P-1008 is conditional on whether the user should be redirected back to a specific flow on login or registration (e.g. job enquiry or whitepaper download)
  const forwarded = context.req.headers['x-forwarded-for'];
  const ip: string | null = typeof forwarded === 'string' ? forwarded.split(/, /)[0] : null;

  // No point paying for a getCountryIsoCode call if we don't have the IP address
  if (!ip) {
    tryToNoticeError(
      new Error('Failed to get IP address from request headers on registration page'),
    );
    return;
  }

  // The enquire_job action was handled first as a separate ticket/feature flag.
  // We have split the logic up as we know that enquire_job works and we don't want to impact that flow
  // This way we maxmise our confidence with deploying the new feature flag and being able to rollback if needed without impacting enquire_job
  if (
    query.action === 'enquire_job' &&
    !(await getProfileRegistrationFromJobEnquiryFlag(context))
  ) {
    return;
  } else if (
    query.action &&
    query.action !== 'enquire_job' &&
    !(await getRedirectRemainingQueryActionToProfileRegistration(context))
  ) {
    return;
  }

  const countryIsoCode = await getCountryFromIpAddress(ip);

  const profileRegistrationAbTest = await getProfileRegistrationAbTestFlag(
    context,
    countryIsoCode ?? '',
  );

  if (!profileRegistrationAbTest) {
    return;
  }

  const practitionerRegistrationUrl = generatePractitionerRegistrationUrl(
    query?.action as string | undefined,
    query?.job_number as string | undefined,
    query?.job_url as string | undefined,
    previousRoute,
  );
  return {
    redirect: {
      destination: practitionerRegistrationUrl,
      permanent: false,
    },
  };
}

export async function getServerSideProps(
  context: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<RegisterPageProps>> {
  const { query } = context;
  enforceSafeRedirectUrl(query.redirect_url);
  const previousRoute = query.redirect_url
    ? decodeURIComponent(query.redirect_url as string)
    : null;
  let whitepaperSlug: WhitepaperSlugs | null = null;

  if (query.whitepaper) {
    if (whitepapers.has(query.whitepaper as WhitepaperSlugs)) {
      whitepaperSlug = query.whitepaper as WhitepaperSlugs;
    } else {
      tryToNoticeError(new Error('Unexpected Whitepaper on Registration'), {
        whitepaper: query.whitepaper as string,
      });
    }
  }

  const [
    isNewRegistrationFlowEnabled,
    isNewRegistrationSpecialityScreenRemoved,
    isNomanEasterEggEnabled,
  ] = await Promise.all([
    getNewRegistrationFlowStatus(context),
    getNewRegistrationSpecialityScreenRemovedStatus(context),
    getNomanEasterEggFlag(context),
  ]);

  const redirect = await getRedirectIfRequired(context, previousRoute);

  if (redirect) {
    return redirect;
  }

  return {
    props: {
      previousRoute,
      preAction: (query.action as RedirectionPreAction) || null,
      referralCode: (query.referralCode as string) || null,
      metadata: {
        title: 'Register and Discover Better',
        description:
          'Register and Discover Better to find Your Dream Medical Job in Australia or New Zealand',
      },
      whitepaperSlug: whitepaperSlug,
      isNewRegistrationFlowEnabled,
      isNewRegistrationSpecialityScreenRemoved,
      isNomanEasterEggEnabled,
    },
  };
}

export default Register;
